import React, { useCallback, useEffect, useState } from 'react';
import { StatesButton as TPAButton, StatesButtonStates, TextButton as TPATextButton, TextButtonPriority } from 'wix-ui-tpa/cssVars';
import classNames from 'classnames';
import { classes } from './submit-button.st.css';
import { FIELD_TYPES, SubmitButtonStyle } from '../../constants';
import { ForcedState, useForcedState } from '../../contexts/forced-state-context';
import { getSubmitButtonStyle, useStyleParams } from '../../contexts/style-params-context';
import { useFieldActions } from '../../contexts/field-actions-context';
import { useConfig } from '../../contexts/config-context';
import ThankYouMessage from './thank-you-message';
import ErrorMessage from './error-message';
import { isValidUrl, getCurrentStep } from './utils';
import { useForm } from '../../contexts/form-context';
import { Spinner } from 'wix-ui-tpa';
import { NextButton } from './next-button';
import { PreviousButton } from './previous-button';
export let SubmitAction = /*#__PURE__*/function (SubmitAction) {
  SubmitAction["THANK_YOU_MESSAGE"] = "THANK_YOU_MESSAGE";
  SubmitAction["EXTERNAL_URL"] = "EXTERNAL_URL";
  return SubmitAction;
}({});
export let ExternalUrlTarget = /*#__PURE__*/function (ExternalUrlTarget) {
  ExternalUrlTarget["BLANK"] = "_blank";
  ExternalUrlTarget["SELF"] = "_self";
  return ExternalUrlTarget;
}({});
const InProgressLoader = () => {
  return /*#__PURE__*/React.createElement(Spinner, {
    className: classes.spinner
  });
};
const Submit = _ref => {
  let {
    onClick,
    disabled,
    submitText,
    hasSteps
  } = _ref;
  const {
    isMobile
  } = useConfig();
  const forcedState = useForcedState();
  const hover = forcedState.includes(ForcedState.SUBMIT_BUTTON_HOVER);
  const textButton = getSubmitButtonStyle(useStyleParams(), isMobile) === SubmitButtonStyle.Text;
  const {
    inProgress
  } = useFieldActions();
  return textButton ? /*#__PURE__*/React.createElement(TPATextButton, {
    priority: TextButtonPriority.primary,
    className: classNames(classes.textButton, {
      [classes.textButtonHover]: hover,
      [classes.navigationButtons]: hasSteps
    }),
    onClick: onClick,
    disabled: disabled
  }, inProgress ? /*#__PURE__*/React.createElement(InProgressLoader, null) : submitText) : /*#__PURE__*/React.createElement(TPAButton, {
    className: classNames(classes.button, {
      [classes.hover]: hover,
      [classes.navigationButtons]: hasSteps
    }),
    upgrade: true,
    onClick: onClick,
    disabled: disabled,
    state: inProgress ? StatesButtonStates.IN_PROGRESS : StatesButtonStates.IDLE,
    inProgressContent: /*#__PURE__*/React.createElement(InProgressLoader, null),
    idleContent: submitText
  });
};
const SubmitButton = _ref2 => {
  let {
    submitText,
    text,
    id,
    thankYouMessageText,
    thankYouMessageDuration,
    externalUrl,
    externalUrlTarget,
    submitAction,
    forceThankYouMessage,
    previousText,
    nextText,
    FieldLayout
  } = _ref2;
  const form = useForm();
  const {
    hasSteps,
    stepNumber,
    isLastStep
  } = getCurrentStep(form, id);
  const {
    inProgress,
    actions
  } = useFieldActions();
  const [showMessage, setShowMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const {
    isMobile
  } = useConfig();
  const textButton = getSubmitButtonStyle(useStyleParams(), isMobile) === SubmitButtonStyle.Text;
  const shouldShowMessage = submitAction === SubmitAction.THANK_YOU_MESSAGE && thankYouMessageText;
  const shouldRedirect = submitAction === SubmitAction.EXTERNAL_URL && externalUrl && isValidUrl(externalUrl);
  const isDisabled = inProgress || submitInProgress;
  useEffect(() => {
    if (showMessage && thankYouMessageDuration) {
      const timer = setTimeout(() => {
        setShowMessage(false);
      }, thankYouMessageDuration * 1000);
      return () => clearTimeout(timer);
    }
    return;
  }, [showMessage, thankYouMessageDuration]);
  const handleRedirect = useCallback(() => {
    const urlWithoutProtocol = externalUrl == null ? void 0 : externalUrl.replace(/^https?:\/\//, '');
    const updatedExternalUrl = "https://" + urlWithoutProtocol;
    window.open(updatedExternalUrl, externalUrlTarget || '_blank');
  }, [externalUrl, externalUrlTarget]);
  const handleSubmitClick = useCallback(async () => {
    setSubmitInProgress(true);
    try {
      const response = await (actions == null ? void 0 : actions.submitForm());
      if (response && shouldShowMessage) {
        setShowMessage(true);
      } else if (response && shouldRedirect) {
        handleRedirect();
      }
      setShowErrorMessage(false);
    } catch (e) {
      setShowErrorMessage(true);
    }
    setSubmitInProgress(false);
  }, [actions, handleRedirect, shouldRedirect, shouldShowMessage]);
  return /*#__PURE__*/React.createElement(FieldLayout, {
    target: id,
    fieldType: FIELD_TYPES.SUBMIT_BUTTON
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.wrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.container
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames({
      [classes.navigationContainer]: hasSteps,
      [classes.textButtonContainer]: !hasSteps && textButton
    })
  }, hasSteps && stepNumber !== 0 && /*#__PURE__*/React.createElement(PreviousButton, {
    previousText: previousText,
    className: classes.navigationButtons
  }), hasSteps && !isLastStep ? /*#__PURE__*/React.createElement(NextButton, {
    nextText: nextText,
    className: classes.navigationButtons
  }) : /*#__PURE__*/React.createElement(Submit, {
    onClick: handleSubmitClick,
    disabled: isDisabled,
    submitText: text || submitText,
    hasSteps: hasSteps
  })), /*#__PURE__*/React.createElement(ThankYouMessage, {
    messageText: thankYouMessageText,
    showMessage: (!hasSteps || isLastStep) && (showMessage || forceThankYouMessage)
  }), showErrorMessage && /*#__PURE__*/React.createElement(ErrorMessage, null))));
};
export default SubmitButton;
//# sourceMappingURL=submit-button.js.map